import { z } from 'zod';
import {
  LogisticsCalculationMoneyTotalsSchema,
  LogisticsCalculationMoneySchema,
  LogisticsCalculationPayableSchema,
} from './calculation.js';
import {
  LOGISTICS_CALCULATION_DECIMAL_MAX,
  LogisticsCalculationCaptionedCostSchema,
  LogisticsCalculationCostSchema,
  LogisticsCalculationCurrencySchema,
  LogisticsCalculationLayoutVersionSchema,
  LogisticsCalculationPricingBasisSchema,
  LogisticsCalculationSectionSchema,
} from './content.js';
import {
  LOGISTICS_CALCULATION_PUBLIC_CODE_REGEX,
  LogisticsCalculationPaymentLabelSchema,
  LogisticsCalculationStyleSchema,
} from './status.js';

/**
 * Raw money the document layout formats itself, next to the frozen
 * `unitPriceDisplay` the `v2` layout projects and the document never reads.
 *
 * INVARIANT: every key is `.optional()`. The DTO is `.strict()` and any parse
 * failure resolves as an opaque not-found, so a live customer link opened while
 * an older backend still serves must not be rejected by a newer web bundle.
 */
const PublicLineAmountShape = {
  unitPriceDisplay: z.string().optional(),
  amount: LogisticsCalculationMoneySchema.optional(),
  rate: z.string().optional(),
  currency: LogisticsCalculationCurrencySchema.nullable().optional(),
  convertedAmountUsd: LogisticsCalculationMoneySchema.nullable().optional(),
} as const;

export const LogisticsCalculationPublicLineSchema = z
  .object({
    serviceName: z.string(),
    quantity: z.string(),
    unit: z.string(),
    ...PublicLineAmountShape,
    amountDisplay: z.string(),
  })
  .strict();
export const LogisticsCalculationPublicCargoLineSchema = z
  .object({
    section: LogisticsCalculationSectionSchema,
    serviceName: z.string(),
    quantity: z.string(),
    unit: z.string(),
    ...PublicLineAmountShape,
    amountDisplay: z.string(),
  })
  .strict();

/** `faceTotals` keeps the untranslated per-currency sum `totals` folds into USD. */
const LogisticsCalculationPublicSectionSchema = z
  .object({
    present: z.boolean(),
    totals: LogisticsCalculationMoneyTotalsSchema,
    faceTotals: LogisticsCalculationMoneyTotalsSchema,
  })
  .strict();

export const LogisticsCalculationPublicCargoSchema = z
  .object({
    article: z.string(),
    name: z.string().nullable(),
    seats: z.string().nullable(),
    weight: z.string().nullable(),
    volume: z.string().nullable(),
    pricingBasis: LogisticsCalculationPricingBasisSchema.nullable().optional(),
    transportType: z.string().nullable(),
    departedAt: z.string().nullable(),
    arrivedAt: z.string().nullable(),
    // Kept optional for rolling compatibility with the backend that still emitted them.
    hasPhoto: z.boolean().optional(),
    photoSelector: z.number().int().nonnegative().nullable().optional(),
    // The cost cells the sheet prints as columns, projected exactly as typed.
    transport: LogisticsCalculationCaptionedCostSchema.optional(),
    insurance: LogisticsCalculationCaptionedCostSchema.optional(),
    packaging: LogisticsCalculationCostSchema.optional(),
    extras: z.array(LogisticsCalculationCaptionedCostSchema).optional(),
    prr: LogisticsCalculationCostSchema.optional(),
    clientDelivery: LogisticsCalculationCostSchema.optional(),
    lines: z.array(LogisticsCalculationPublicCargoLineSchema),
    sections: z
      .object({
        chinaLogistics: LogisticsCalculationPublicSectionSchema,
        chinaExpenses: LogisticsCalculationPublicSectionSchema,
        rfExpenses: LogisticsCalculationPublicSectionSchema,
        rfLogistics: LogisticsCalculationPublicSectionSchema,
      })
      .strict()
      .optional(),
    totals: LogisticsCalculationMoneyTotalsSchema,
  })
  .strict();

/**
 * The payable block plus the divisor the document divides by, when it has one.
 *
 * INVARIANT: the key is absent, never `null` or `''`. The document reads its
 * presence as "this payment has a formula", so an empty value would print a
 * division by nothing. The bound and the positive-decimal rule together restate
 * `content.ts`'s own `paymentDivisor` constraint at the public boundary: an
 * individual payment stores no divisor, a zero would divide the customer's
 * total by nothing, and no stored divisor is longer than a typed number.
 */
const PublicPayableSchema = LogisticsCalculationPayableSchema.extend({
  currency: z.enum(['RUB', 'USD', 'USD_RUB']).optional(),
  divisor: z
    .string()
    .max(LOGISTICS_CALCULATION_DECIMAL_MAX)
    .regex(/^(?:0*[1-9]\d*)(?:\.\d+)?$|^0*\.\d*[1-9]\d*$/, 'must be a positive decimal')
    .optional(),
});

/** @aiSecurity the only shape token-authorized customer endpoints may return. */
export const LogisticsCalculationPublicDtoSchema = z
  .object({
    style: LogisticsCalculationStyleSchema,
    layoutVersion: LogisticsCalculationLayoutVersionSchema.optional(),
    publicCode: z.string().regex(LOGISTICS_CALCULATION_PUBLIC_CODE_REGEX),
    documentDate: z.string(),
    payDueDate: z.string(),
    client: z
      .object({ company: z.string(), contactName: z.string(), contactPhone: z.string() })
      .strict(),
    manager: z
      .object({
        name: z.string(),
        phone: z.string(),
        telegram: z.string().nullable().optional(),
        note: z.string().nullable(),
      })
      .strict(),
    paymentLabel: LogisticsCalculationPaymentLabelSchema,
    paymentLabelDisplay: z.string(),
    cnyPerUsdRate: z.string().optional(),
    rubPerUsdRate: z.string().optional(),
    rubPerCnyRate: z.string().optional(),
    cargos: z.array(LogisticsCalculationPublicCargoSchema),
    commonServices: z.array(LogisticsCalculationPublicLineSchema),
    commonServicesTotals: LogisticsCalculationMoneyTotalsSchema.optional(),
    commonServicesFaceTotals: LogisticsCalculationMoneyTotalsSchema.optional(),
    totals: LogisticsCalculationMoneyTotalsSchema,
    grandTotalUsd: LogisticsCalculationMoneySchema,
    grandTotalRub: LogisticsCalculationMoneySchema,
    payable: PublicPayableSchema.optional(),
    allRubReference: LogisticsCalculationMoneySchema.nullable(),
  })
  .strict();
export type LogisticsCalculationPublicDto = z.infer<typeof LogisticsCalculationPublicDtoSchema>;

export const LOGISTICS_CALCULATION_PUBLIC_FORBIDDEN_FIELDS = [
  'internalCost',
  'sourceMetadata',
  'field',
  'value',
  'expenseSnapshot',
  'sourceCargoArticleId',
  'ownerId',
  'publicToken',
  'publicTokenHash',
  'photoObjectKey',
  'objectKey',
  'storageObjectKey',
  'storageKey',
  'rawObjectKey',
  'rawStorageKey',
  'carrier',
  'carrierId',
  'carrierName',
  'carrierUuid',
] as const;
